import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Grid,
  TextField,
  Card,
  CardContent,
  Divider,
  Alert,
  Snackbar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  Save,
  Preview,
  Image,
  Add,
  Delete,
  Edit,
} from '@mui/icons-material';
import { contentApi } from '../services/api';
import { FileUpload } from './components/FileUpload';

interface ContentSection {
  id: string;
  title: string;
  content: string;
  imageUrl?: string;
}

interface ContentEditorProps {
  section: string;
  title: string;
}

const modules = {
  toolbar: [
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'indent': '-1'}, { 'indent': '+1' }],
    [{ 'align': [] }],
    ['link', 'image'],
    ['clean']
  ],
};

const formats = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'list', 'bullet', 'indent',
  'align', 'link', 'image'
];

export const ContentEditor: React.FC<ContentEditorProps> = ({ section, title }) => {
  const [sections, setSections] = useState<ContentSection[]>([]);
  const [selectedSection, setSelectedSection] = useState<string>('');
  const [editingSection, setEditingSection] = useState<ContentSection | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadContent();
  }, [section]);

  const loadContent = async () => {
    setLoading(true);
    try {
      const data = await contentApi.get(section);
      if (data && Array.isArray(data)) {
        setSections(data);
      } else {
        // 기본 섹션 생성
        const defaultSections = getDefaultSections(section);
        setSections(defaultSections);
      }
    } catch (error) {
      console.error('콘텐츠 로드 실패:', error);
      setErrorMessage('콘텐츠를 불러오는데 실패했습니다.');
      setShowError(true);
      // 기본 섹션 생성
      const defaultSections = getDefaultSections(section);
      setSections(defaultSections);
    } finally {
      setLoading(false);
    }
  };

  const getDefaultSections = (sectionType: string): ContentSection[] => {
    switch (sectionType) {
      case 'home':
        return [
          {
            id: 'hero',
            title: '메인 배너',
            content: '<h1>환영합니다</h1><p>귀하의 비즈니스를 위한 완벽한 솔루션을 제공합니다.</p>',
          },
          {
            id: 'services',
            title: '서비스 소개',
            content: '<h2>우리의 서비스</h2><p>고품질 서비스를 제공합니다.</p>',
          },
        ];
      case 'about':
        return [
          {
            id: 'company',
            title: '회사 소개',
            content: '<h2>회사 소개</h2><p>저희 회사는...</p>',
          },
          {
            id: 'vision',
            title: '비전',
            content: '<h2>비전</h2><p>우리의 비전은...</p>',
          },
        ];
      case 'portfolio':
        return [
          {
            id: 'projects',
            title: '프로젝트',
            content: '<h2>주요 프로젝트</h2><p>성공적으로 완료한 프로젝트들을 소개합니다.</p>',
          },
        ];
      case 'contact':
        return [
          {
            id: 'contact_info',
            title: '연락처 정보',
            content: '<h2>연락처</h2><p>언제든 연락주세요.</p>',
          },
        ];
      default:
        return [];
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await contentApi.update(section, sections);
      setShowSuccess(true);
    } catch (error) {
      console.error('저장 실패:', error);
      setErrorMessage('저장에 실패했습니다.');
      setShowError(true);
    } finally {
      setSaving(false);
    }
  };

  const handleAddSection = () => {
    const newSection: ContentSection = {
      id: `section_${Date.now()}`,
      title: '새 섹션',
      content: '<p>새로운 콘텐츠를 입력하세요.</p>',
    };
    setSections([...sections, newSection]);
  };

  const handleDeleteSection = (sectionId: string) => {
    setSections(sections.filter(s => s.id !== sectionId));
  };

  const handleEditSection = (sectionItem: ContentSection) => {
    setEditingSection(sectionItem);
    setSelectedSection(sectionItem.id);
  };

  const handleSectionContentChange = (content: string) => {
    if (editingSection) {
      const updatedSection = { ...editingSection, content };
      setEditingSection(updatedSection);
      setSections(sections.map(s => s.id === editingSection.id ? updatedSection : s));
    }
  };

  const handleSectionTitleChange = (title: string) => {
    if (editingSection) {
      const updatedSection = { ...editingSection, title };
      setEditingSection(updatedSection);
      setSections(sections.map(s => s.id === editingSection.id ? updatedSection : s));
    }
  };

  const handleImageUpload = (fileUrl: string) => {
    if (editingSection) {
      const updatedSection = { ...editingSection, imageUrl: fileUrl };
      setEditingSection(updatedSection);
      setSections(sections.map(s => s.id === editingSection.id ? updatedSection : s));
    }
  };

  const handleAddImage = () => {
    if (editingSection && newImageUrl) {
      const updatedSection = { ...editingSection, imageUrl: newImageUrl };
      setEditingSection(updatedSection);
      setSections(sections.map(s => s.id === editingSection.id ? updatedSection : s));
      setNewImageUrl('');
      setShowImageDialog(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          콘텐츠를 불러오는 중...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          {title}
        </Typography>
        <Box>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <Save />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? '저장 중...' : '저장'}
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* 섹션 목록 */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, height: 'fit-content' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">섹션 목록</Typography>
              <Button
                size="small"
                startIcon={<Add />}
                onClick={handleAddSection}
              >
                추가
              </Button>
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {sections.map((section) => (
                <Card
                  key={section.id}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: selectedSection === section.id ? 'primary.light' : 'background.paper',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                  onClick={() => handleEditSection(section)}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle2" noWrap>
                        {section.title}
                      </Typography>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSection(section.id);
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* 편집 영역 */}
        <Grid item xs={12} md={9}>
          {editingSection ? (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {editingSection.title} 편집
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="섹션 제목"
                    value={editingSection.title}
                    onChange={(e) => handleSectionTitleChange(e.target.value)}
                    variant="outlined"
                    sx={{ mb: 3 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    콘텐츠 편집
                  </Typography>
                  <ReactQuill
                    theme="snow"
                    value={editingSection.content}
                    onChange={handleSectionContentChange}
                    modules={modules}
                    formats={formats}
                    style={{ height: '300px', marginBottom: '50px' }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    섹션 이미지
                  </Typography>
                  
                  {editingSection.imageUrl && (
                    <Box sx={{ mb: 2 }}>
                      <img
                        src={editingSection.imageUrl}
                        alt="섹션 이미지"
                        style={{
                          maxWidth: '100%',
                          maxHeight: 200,
                          objectFit: 'cover',
                          borderRadius: 4,
                        }}
                      />
                    </Box>
                  )}
                  
                  <FileUpload
                    onUploadComplete={handleImageUpload}
                    accept="image/*"
                    title="섹션 이미지 업로드"
                    description="섹션에 표시할 이미지를 업로드하세요"
                  />
                </Grid>
              </Grid>
            </Paper>
          ) : (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6" color="textSecondary">
                편집할 섹션을 선택하세요
              </Typography>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* 성공 알림 */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
      >
        <Alert severity="success" onClose={() => setShowSuccess(false)}>
          콘텐츠가 성공적으로 저장되었습니다.
        </Alert>
      </Snackbar>

      {/* 에러 알림 */}
      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
      >
        <Alert severity="error" onClose={() => setShowError(false)}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}; 